/**
 * Download-once model store for the on-device background segmenter.
 *
 * The model is fetched from the public Cloudflare R2 bucket EXACTLY ONCE and cached in
 * IndexedDB, which **survives app auto-updates** (updates replace the app binary,
 * not IndexedDB). It re-downloads only when the model VERSION changes, the cache is
 * cleared (admin / invalid session), or the app is uninstalled.
 *
 * IndexedDB (not localStorage) because the model is multi-MB binary. Used on desktop
 * (where the native `ml` build consumes it); on web there's no consumer (classical
 * fallback), so callers just won't request it.
 *
 * SWAPPING THE MODEL: the native runtime (segment.rs) is model-agnostic — it reads the
 * input name + spatial size from the ONNX graph — so a swap is just config:
 *   1. Pick a FIXED-input, single-output ONNX (e.g. 1024²) so segment.rs auto-detects
 *      the size; otherwise set GENZ_DEFAULT_SIDE / DEFAULT_SIDE in segment.rs to match.
 *   2. Upload the `.onnx` to the public R2 bucket under `models/<name>.onnx`.
 *   3. Bump the model's `version` (forces a one-time re-download) + set its `file` to the
 *      new object name. The BASE comes from the cloud (`GENZ_R2_MODELS_BASE`, served in
 *      /api/customization/capabilities) with the built-in bucket as the fallback (D-18),
 *      so MOVING the bucket is a config change rather than a client release.
 * Note BiRefNet (~109 MB lite fp16) is much larger than U²-Net-p (~4.5 MB) — the
 * download-once IndexedDB cache keeps that a one-time cost per device/version. See
 * docs/segmentation-model.md for the full hosting recipe + the sigmoid post-processing.
 */

/** A selectable on-device segmentation model. */
export type SegModelId = "birefnet" | "birefnet_full" | "u2netp";
interface SegModel {
  id: SegModelId;
  version: string;
  /** Object name only — the base comes from `modelsBase()` at fetch time (D-18). */
  file: string;
}

/** Public Cloudflare R2 bucket the segmenter models shipped on. Kept as the FALLBACK:
 *  a desktop talking to a cloud that predates WS-G (or one with GENZ_R2_MODELS_BASE
 *  unset) must still find its model (D-18). */
export const DEFAULT_MODELS_BASE = "https://pub-db2bad57aa984c4f8651f4f8f9b137a5.r2.dev/models";

let modelsBaseOverride: string | null = null;

/** Point the store at the base the cloud named in /api/customization/capabilities.
 *  Ignores anything that is not an https:// URL — a model is 109-489 MB of code-adjacent
 *  data and must not be fetched over plain http from a value the app was handed. */
export function setModelsBase(base: string | null): void {
  const trimmed = (base ?? "").trim().replace(/\/+$/, "");
  modelsBaseOverride = trimmed.startsWith("https://") ? trimmed : null;
}

export function modelsBase(): string {
  return modelsBaseOverride ?? DEFAULT_MODELS_BASE;
}

/** Absolute URL for one model, resolved against the CURRENT base. */
export function segModelUrl(m: SegModel): string {
  return `${modelsBase()}/${m.file}`;
}

/**
 * Model registry. BiRefNet is the DEFAULT (cleanest matte on glass / transparent /
 * reflective products). Both ONNX models are hosted on Cloudflare R2 (above):
 *   - birefnet → BiRefNet_lite fp16, MIT (onnx-community/BiRefNet_lite-ONNX), fixed
 *     1024² single saliency output (segment.rs auto-detects the size; ~109 MB).
 *   - u2netp → the validated ~4.5 MB fallback (`setSegModel("u2netp")`).
 * BiRefNet emits raw logits → segment.rs applies a sigmoid (not min-max) for the matte.
 */
const MODELS: Record<SegModelId, SegModel> = {
  birefnet: {
    id: "birefnet",
    version: "birefnet-lite-fp16-r2-v1",
    file: "birefnet_lite_1024_fp16.onnx",
  },
  // Full BiRefNet fp16 (onnx-community/BiRefNet-ONNX), fixed 1024² single output, ~489 MB.
  // f32 I/O + raw logits → segment.rs sigmoid path applies unchanged (validated drop-in).
  // Selectable by the super-admin for sharper clear-edge mattes at a larger one-time download.
  birefnet_full: {
    id: "birefnet_full",
    version: "birefnet-full-fp16-r2-v1",
    file: "birefnet_full_1024_fp16.onnx",
  },
  u2netp: {
    id: "u2netp",
    version: "u2netp-v1",
    file: "u2netp_simp.onnx",
  },
};
// Default to BiRefNet **lite** (~109 MB) — much faster on-device inference than full
// (~489 MB at 1024²), so "Remove background (AI)" isn't a multi-second stall. The super-admin
// can still select `birefnet_full` in the Segmenter tab for sharper clear-edge mattes.
const DEFAULT_MODEL: SegModelId = "birefnet";
/** Abort the download only on a real STALL — i.e. no bytes received for this long. Reset
 *  on every chunk, so an arbitrarily large model (full BiRefNet ~489 MB) downloads fine on a
 *  slow-but-steady link, but a dead connection still fails fast instead of hanging forever. */
const STALL_TIMEOUT_MS = 60_000;
const DB = "genz-models";
const STORE = "blobs";
const LS_KEY = "genz-seg-model";

let cached: Uint8Array | null = null;
let inflight: Promise<Uint8Array | null> | null = null;
/** Runtime override (set by the admin calibration apply-path after fetching the global
 *  setting); falls back to a persisted localStorage choice, then the default. */
let overrideModel: SegModelId | null = null;

/** The active segmentation model = runtime override → persisted choice → default. */
function activeModel(): SegModel {
  if (overrideModel && MODELS[overrideModel]) return MODELS[overrideModel];
  try {
    const ls = typeof localStorage !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    if (ls && MODELS[ls as SegModelId]) return MODELS[ls as SegModelId];
  } catch { /* ignore */ }
  return MODELS[DEFAULT_MODEL];
}

/** The id of the active segmentation model (for UI / telemetry). */
export function activeSegModelId(): SegModelId {
  return activeModel().id;
}

/** Absolute URL the active model would be fetched from, against the current base. */
export function activeSegModelUrl(): string {
  return segModelUrl(activeModel());
}

/**
 * Select the active segmentation model and persist the choice (per device). Drops the
 * in-memory cache so the next request resolves the new model's blob. The admin
 * calibration apply-path calls this from the global setting so every client uses the
 * model the admin chose. Pass null to revert to the persisted/default selection.
 */
export function setSegModel(id: SegModelId | null): void {
  overrideModel = id;
  cached = null;
  inflight = null;
  try {
    if (typeof localStorage !== "undefined") {
      if (id) localStorage.setItem(LS_KEY, id);
      else localStorage.removeItem(LS_KEY);
    }
  } catch { /* ignore */ }
}

/** Resolve `null` if `p` hasn't settled within `ms` — an IndexedDB open/read/write that
 *  never settles (blocked upgrade, wedged transaction) must DEGRADE (skip the cache /
 *  re-download) instead of freezing the AI-removal flow forever behind an un-catchable
 *  never-resolving await (the Windows "gets stuck, nothing works" hang, cause H1/H5). */
function withIdbTimeout<T>(p: Promise<T>, ms = 10_000): Promise<T | null> {
  return Promise.race([p, new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))]);
}

function idb(): Promise<IDBDatabase | null> {
  return withIdbTimeout(new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      // Another tab/window holds an old connection open → the open request stalls
      // indefinitely with NO error event. Degrade to "no cache" rather than hang.
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  }));
}

function idbGet(db: IDBDatabase, key: string): Promise<ArrayBuffer | null> {
  return withIdbTimeout(new Promise<ArrayBuffer | null>((resolve) => {
    try {
      const r = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      r.onsuccess = () => resolve((r.result as ArrayBuffer) ?? null);
      r.onerror = () => resolve(null);
    } catch {
      resolve(null); // a synchronous transaction throw must not become an unhandled rejection
    }
  }));
}

function idbPut(db: IDBDatabase, key: string, val: ArrayBuffer): Promise<void> {
  return withIdbTimeout(new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  }), 30_000).then(() => undefined);
}

/** Progress of an in-flight model download (drives the Update button's progress bar). */
export interface DownloadProgress {
  /** Bytes received so far. */
  loaded: number;
  /** Total bytes (Content-Length), or 0 when the server didn't send it. */
  total: number;
  /** Fraction 0..1 (0 when `total` is unknown). */
  pct: number;
  /** Current download speed in bytes/sec. */
  bytesPerSec: number;
  /** Estimated seconds remaining (0 when `total` is unknown). */
  etaSec: number;
}

/**
 * Stream the model body to bytes — reporting progress and RESETTING a stall timer on every
 * chunk — then cache it. Streaming (vs one big `arrayBuffer()`) keeps the UI responsive and
 * makes %/speed/ETA observable; the per-chunk stall reset lets a slow-but-steady link finish
 * while a dead connection still aborts fast.
 */
async function streamAndCache(
  model: SegModel,
  key: string,
  db: IDBDatabase | null,
  onProgress?: (p: DownloadProgress) => void,
): Promise<Uint8Array | null> {
  const ac = new AbortController();
  let stall = setTimeout(() => ac.abort(), STALL_TIMEOUT_MS);
  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
  try {
    const res = await fetch(segModelUrl(model), { signal: ac.signal });
    if (!res.ok || !res.body) return null;
    const total = Number(res.headers.get("content-length")) || 0;
    const reader = res.body.getReader();
    const start = now();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    let lastEmit = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      clearTimeout(stall);
      stall = setTimeout(() => ac.abort(), STALL_TIMEOUT_MS);
      if (!value) continue;
      chunks.push(value);
      loaded += value.length;
      const t = now();
      // Throttle progress to ~5/sec to avoid render thrash (always emit the final tick below).
      if (onProgress && t - lastEmit > 200) {
        lastEmit = t;
        const bps = loaded / Math.max((t - start) / 1000, 0.001);
        const pct = total ? Math.min(1, loaded / total) : 0;
        onProgress({ loaded, total, pct, bytesPerSec: bps, etaSec: total && bps ? Math.max(0, (total - loaded) / bps) : 0 });
      }
    }
    if (loaded < 1000) return null;
    const buf = new Uint8Array(loaded);
    let off = 0;
    for (const c of chunks) {
      buf.set(c, off);
      off += c.length;
    }
    if (db) await idbPut(db, key, buf.buffer as ArrayBuffer);
    onProgress?.({ loaded, total: total || loaded, pct: 1, bytesPerSec: 0, etaSec: 0 });
    return buf; // caller decides whether to warm the in-memory `cached` (active model only)
  } catch {
    return null; // network / abort / offline → caller handles (fallback / error message)
  } finally {
    clearTimeout(stall);
  }
}

/**
 * Get the segmentation model bytes — from memory, then IndexedDB, then (streamed once from)
 * the network with optional progress. Returns null if it can't be obtained.
 */
export function getSegmentationModel(
  onProgress?: (p: DownloadProgress) => void,
): Promise<Uint8Array | null> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = (async () => {
    // NB: `inflight` is cleared in the OUTER finally — clearing only around streamAndCache
    // meant a failure ABOVE it (idb()/idbGet throwing or wedging) poisoned `inflight` with
    // the same rejected/stuck promise for the process lifetime (restart-only recovery).
    const model = activeModel();
    const db = await idb();
    const key = `model:${model.version}`;
    if (db) {
      const hit = await idbGet(db, key);
      if (hit && hit.byteLength > 0) {
        cached = new Uint8Array(hit);
        return cached;
      }
    }
    const buf = await streamAndCache(model, key, db, onProgress);
    if (buf) cached = buf; // this IS the active model, so warm the in-memory cache
    return buf;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Whether a SPECIFIC model id is already cached locally (independent of the active
 *  selection) — drives the admin Segmenter tab's "downloaded / not downloaded" badge. */
export async function isModelCachedById(id: SegModelId): Promise<boolean> {
  const m = MODELS[id];
  if (!m) return false;
  const db = await idb();
  if (!db) return false;
  const hit = await idbGet(db, `model:${m.version}`);
  return !!hit && hit.byteLength > 0;
}

/** Download a SPECIFIC model by id (the admin Segmenter tab's Download button), streamed
 *  with progress and cached under its version key. If it's the active model the in-memory
 *  cache is warmed too. Resolves true once cached (or already cached). */
export async function downloadModelById(
  id: SegModelId,
  onProgress?: (p: DownloadProgress) => void,
): Promise<boolean> {
  const m = MODELS[id];
  if (!m) return false;
  const db = await idb();
  const key = `model:${m.version}`;
  if (db) {
    const hit = await idbGet(db, key);
    if (hit && hit.byteLength > 0) return true;
  }
  const buf = await streamAndCache(m, key, db, onProgress);
  if (buf && activeModel().id === id) cached = buf;
  return !!buf;
}

/** Per-id in-memory cache for the editor's fast/quality toggle, kept SEPARATE from the active
 *  model's `cached` so switching quality doesn't evict the platform default. */
const cachedById: Partial<Record<SegModelId, Uint8Array>> = {};

/** Get a SPECIFIC model's bytes (memory → IndexedDB → streamed once). Lets the editor pick a
 *  fast vs quality matte without disturbing the admin-selected active model. */
export async function getModelBytesById(
  id: SegModelId,
  onProgress?: (p: DownloadProgress) => void,
): Promise<Uint8Array | null> {
  if (cachedById[id]) return cachedById[id]!;
  if (id === activeModel().id && cached) return cached;
  const m = MODELS[id];
  if (!m) return null;
  const db = await idb();
  const key = `model:${m.version}`;
  if (db) {
    const hit = await idbGet(db, key);
    if (hit && hit.byteLength > 0) {
      cachedById[id] = new Uint8Array(hit);
      return cachedById[id]!;
    }
  }
  const buf = await streamAndCache(m, key, db, onProgress);
  if (buf) cachedById[id] = buf;
  return buf;
}

/** Model bytes for the editor's per-image matte quality: `fast` = U²-Net-p (~4.5 MB, ~1s),
 *  `quality` = the platform default (BiRefNet — cleanest edge, ~12s on CPU). */
export function getSegmentationModelForQuality(
  quality: "fast" | "quality",
  onProgress?: (p: DownloadProgress) => void,
): Promise<Uint8Array | null> {
  return quality === "fast" ? getModelBytesById("u2netp", onProgress) : getSegmentationModel(onProgress);
}

/** Which model id a quality resolves to (for the editor's diagnostic line + cache probes). */
export function modelIdForQuality(quality: "fast" | "quality"): SegModelId {
  return quality === "fast" ? "u2netp" : activeSegModelId();
}

/** Human-readable download speed (MB/s ≥ 1 MB/s, else KB/s). */
export function formatSpeed(bytesPerSec: number): string {
  const mbps = bytesPerSec / (1024 * 1024);
  return mbps >= 1 ? `${mbps.toFixed(1)} MB/s` : `${Math.max(1, Math.round(bytesPerSec / 1024))} KB/s`;
}
/** Human-readable ETA (minutes ≥ 60s, else seconds). */
export function formatEta(sec: number): string {
  return sec >= 60 ? `${Math.round(sec / 60)}m` : `${Math.max(1, Math.round(sec))}s`;
}

/**
 * Explicitly download the active model with progress — the Update button calls this so the
 * big one-time fetch is a DELIBERATE, VISIBLE action with a progress bar, never a silent
 * block on first editor use (which made the app look frozen). Resolves true once cached.
 */
export async function downloadSegmentationModel(
  onProgress?: (p: DownloadProgress) => void,
): Promise<boolean> {
  return !!(await getSegmentationModel(onProgress));
}

/** Whether the model is already cached locally (no network needed). */
export async function isModelCached(): Promise<boolean> {
  if (cached) return true;
  const db = await idb();
  if (!db) return false;
  const hit = await idbGet(db, `model:${activeModel().version}`);
  return !!hit && hit.byteLength > 0;
}

/** Clear the cached model (admin cache-clear / sign-out). */
export async function clearModelCache(): Promise<void> {
  cached = null;
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}
